import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    annotationPath,
    annotationStrokeWidth,
    captureRect,
    denormalizePoint,
    laserSegments,
    normalizePoint,
    type IAnnotation,
} from '@srm/screen-sharing/light';
import SupportPresenceLayer, {
    SupportPresenceCursor,
    type ISupportPresenceAnnotation,
    type ISupportPresenceBeacon,
    type ISupportPresenceMark,
    type ISupportPresencePoint,
    type ISupportPresenceRect,
    type SupportPresenceTone,
} from '@molecules/SupportPresenceLayer';
import { useLatest } from '@srm/ui/hooks/useLatest';
import { userInput } from '../useRemoteControlInputBlock';
import {
    RIPPLE_DURATION,
    shortPeerName as shortName,
    supportOverlayRoot,
    type ISupportPresenceBus,
} from '../supportPresence';

/** Обводка поля отступает от его рамки. */
const FOCUS_GAP = 3;

/** Сотрудник показа: подпись курсора и цвет его пометок. */
export interface ISupportPresencePerson {
    name: string;
    color: string;
}

export interface ISupportPresenceOverlayProps {
    presence: ISupportPresenceBus;
    tone: SupportPresenceTone;
    /** Подпись курсора сотрудника, которого ещё нет среди участников */
    name: string;
    /** Сотрудники показа по `peerId`: имя у курсора и подписи, цвет курсора и пометок */
    people?: Record<string, ISupportPresencePerson>;
    /** Корень захвата: координаты — от него; без него — от окна */
    area: (() => Element | null) | undefined;
    /** Захват не ограничен: курсор попадает в видео эхом — тонкий и без подписи */
    plain: boolean;
    /** Сотрудник управляет: обводка поля, в которое он вводит */
    control: boolean;
    /** Сотрудник подключён: свой курсор и клики пользователя уходят ему */
    watched: boolean;
    sendCursor: (x: number, y: number, visible?: boolean) => void;
    sendClickMark: (x: number, y: number) => void;
}

const editable = (target: EventTarget | null): target is HTMLElement =>
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    (target instanceof HTMLElement && target.isContentEditable);

type Project = (x: number, y: number) => ISupportPresencePoint;

/**
 * Пометки пакета, кроме лазера, → фигуры слоя в координатах окна. Не зависят от времени и курсоров: считаются только
 * при смене пометок и участников (вид при прокрутке и смене размера пакет сам очищает). Толщина линий — от ширины
 * области захвата (`pictureWidth`), как у сотрудника.
 */
function projectAnnotations(
    items: readonly IAnnotation[],
    project: Project,
    people: Record<string, ISupportPresencePerson>,
    pictureWidth: number,
): { shapes: ISupportPresenceAnnotation[]; beacons: ISupportPresenceBeacon[] } {
    const shapes: ISupportPresenceAnnotation[] = [];
    const beacons: ISupportPresenceBeacon[] = [];
    items.forEach((item, index) => {
        const person = people[item.author];
        const color = person?.color ?? 'var(--support-presence-accent)';
        const id = `${item.author}:${item.id}`;
        if (item.kind === 'beacon') {
            beacons.push({
                id: item.createdAt * 100 + index,
                ...project(item.points[0], item.points[1]),
                color,
                name: person?.name,
            });
        } else if (item.kind === 'text') {
            shapes.push({
                id,
                kind: 'text',
                color,
                text: item.text ?? '',
                author: person?.name,
                ...project(item.points[0], item.points[1]),
            });
        } else if (item.kind === 'step') {
            shapes.push({
                id,
                kind: 'step',
                color,
                number: item.stepNumber ?? 1,
                ...project(item.points[0], item.points[1]),
            });
        } else if (item.kind !== 'laser') {
            const width = annotationStrokeWidth(item, pictureWidth);
            shapes.push({
                id,
                kind: 'path',
                color,
                d: annotationPath(item, project, width),
                width,
                fill: item.kind === 'rect' || item.kind === 'ellipse',
                highlight: item.kind === 'highlighter',
            });
        }
    });
    return { shapes, beacons };
}

/** Лазер гаснет за 1,2 с: его сегменты — на момент `now`, каждый кадр. */
const projectLasers = (
    items: readonly IAnnotation[],
    project: Project,
    people: Record<string, ISupportPresencePerson>,
    now: number,
): ISupportPresenceAnnotation[] =>
    items
        .filter((item) => item.kind === 'laser')
        .map((item) => ({
            id: `${item.author}:${item.id}`,
            kind: 'laser',
            color: people[item.author]?.color ?? 'var(--support-presence-accent)',
            segments: laserSegments(item, now, project),
        }));

interface IPresenceCursorsProps {
    presence: ISupportPresenceBus;
    area: (() => Element | null) | undefined;
    people: Record<string, ISupportPresencePerson>;
    /** Подпись курсора сотрудника, которого ещё нет среди участников */
    name: string;
    plain: boolean;
}

/** Курсоры сотрудников — до 30 раз в секунду: своё состояние, остальной слой при этом не перерисовывается. */
function PresenceCursors({ presence, area, people, name, plain }: IPresenceCursorsProps) {
    const [cursors, setCursors] = useState<Record<string, ISupportPresencePoint>>({});
    const areaRef = useLatest(area);
    useEffect(
        () =>
            presence.subscribe((event) => {
                if (event.kind !== 'cursor') return;
                const point = event.cursor
                    ? denormalizePoint(event.cursor.x, event.cursor.y, areaRef.current?.())
                    : null;
                setCursors((map) => {
                    if (!point && !(event.peerId in map)) return map;
                    const next = { ...map };
                    if (point) next[event.peerId] = point;
                    else delete next[event.peerId];
                    return next;
                });
            }),
        [presence, areaRef],
    );
    return Object.entries(cursors).map(([peerId, point]) => (
        <SupportPresenceCursor
            key={peerId}
            point={point}
            name={people[peerId] ? shortName(people[peerId].name) : name}
            color={people[peerId]?.color}
            plain={plain}
        />
    ));
}

const NOBODY: Record<string, ISupportPresencePerson> = {};

const sameRect = (a: ISupportPresenceRect | null, b: ISupportPresenceRect | null) =>
    a === b || (!!a && !!b && a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height);

/**
 * Присутствие сотрудников у пользователя: курсоры, рябь кликов, пометки и кольца «Смотрите сюда», обводка поля ввода —
 * и обратный канал: курсор и доверенные клики самого пользователя. Рисуется в узле рядом с `body`, вне корня захвата.
 */
const SupportPresenceOverlay = ({
    presence,
    tone,
    name,
    people = NOBODY,
    area,
    plain,
    control,
    watched,
    sendCursor,
    sendClickMark,
}: ISupportPresenceOverlayProps) => {
    const [annotations, setAnnotations] = useState<readonly IAnnotation[]>([]);
    const [now, setNow] = useState(() => Date.now());
    const [ripples, setRipples] = useState<ISupportPresenceMark[]>([]);
    const [focus, setFocus] = useState<ISupportPresenceRect | null>(null);
    const areaRef = useLatest(area);
    const send = useLatest({ sendCursor, sendClickMark });
    const nextId = useRef(0);
    const peopleRef = useLatest(people);

    useEffect(() => {
        const timers = new Set<ReturnType<typeof setTimeout>>();
        const later = (action: () => void, ms: number) => {
            const timer = setTimeout(() => {
                timers.delete(timer);
                action();
            }, ms);
            timers.add(timer);
        };
        const place = (x: number, y: number) => denormalizePoint(x, y, areaRef.current?.());
        const unsubscribe = presence.subscribe((event) => {
            if (event.kind === 'annotations') {
                setAnnotations(event.annotations);
            } else if (event.kind === 'click') {
                const color = peopleRef.current[event.peerId]?.color;
                const ripple = {
                    id: ++nextId.current,
                    ...place(event.mark.x, event.mark.y),
                    ...(color ? { color } : {}),
                };
                setRipples((list) => [...list.slice(-4), ripple]);
                later(() => setRipples((list) => list.filter((item) => item.id !== ripple.id)), RIPPLE_DURATION);
            }
        });
        return () => {
            unsubscribe();
            timers.forEach(clearTimeout);
        };
    }, [presence, areaRef, peopleRef]);

    // Лазер гаснет за 1,2 с: пока он на экране, слой перерисовывается каждый кадр.
    const lasers = annotations.some((item) => item.kind === 'laser');
    useEffect(() => {
        if (!lasers) return undefined;
        let frame = requestAnimationFrame(function tick() {
            setNow(Date.now());
            frame = requestAnimationFrame(tick);
        });
        return () => cancelAnimationFrame(frame);
    }, [lasers]);

    const drawn = useMemo(
        () =>
            projectAnnotations(
                annotations,
                (x, y) => denormalizePoint(x, y, areaRef.current?.()),
                people,
                captureRect(areaRef.current?.()).width,
            ),
        [annotations, people, areaRef],
    );
    const shapes = lasers
        ? [
              ...drawn.shapes,
              ...projectLasers(annotations, (x, y) => denormalizePoint(x, y, areaRef.current?.()), people, now),
          ]
        : drawn.shapes;

    // Поле, в которое вводит сотрудник: его команды — недоверенные события input.
    useEffect(() => {
        if (!control) {
            setFocus(null);
            return undefined;
        }
        let target: HTMLElement | null = null;
        let frame = 0;
        let last: ISupportPresenceRect | null = null;
        const measure = () => {
            frame = 0;
            const rect = target?.isConnected ? target.getBoundingClientRect() : null;
            const next =
                rect && rect.width > 0
                    ? {
                          left: rect.left - FOCUS_GAP,
                          top: rect.top - FOCUS_GAP,
                          width: rect.width + FOCUS_GAP * 2,
                          height: rect.height + FOCUS_GAP * 2,
                      }
                    : null;
            if (!next) target = null;
            if (!sameRect(next, last)) setFocus(next);
            last = next;
            // Поле едет вместе с прокруткой и раскладкой — следим, пока оно в фокусе сотрудника.
            if (target) frame = requestAnimationFrame(measure);
        };
        const onInput = (event: Event) => {
            if (userInput.is(event) || !editable(event.target)) return;
            target = event.target;
            if (!frame) frame = requestAnimationFrame(measure);
        };
        const onFocusOut = (event: FocusEvent) => {
            if (event.target !== target) return;
            target = null;
            if (!frame) frame = requestAnimationFrame(measure);
        };
        window.addEventListener('input', onInput, true);
        window.addEventListener('focusout', onFocusOut, true);
        return () => {
            window.removeEventListener('input', onInput, true);
            window.removeEventListener('focusout', onFocusOut, true);
            cancelAnimationFrame(frame);
            setFocus(null);
        };
    }, [control]);

    // Курсор и клики самого пользователя — сотруднику.
    useEffect(() => {
        if (!watched) return undefined;
        // Колбэки показа стабильны: уход со страницы сообщается тем же, что был при подписке.
        const hide = send.current.sendCursor;
        const toArea = (event: MouseEvent) => normalizePoint(event.clientX, event.clientY, areaRef.current?.());
        const onMove = (event: PointerEvent) => {
            if (!userInput.is(event)) return;
            const point = toArea(event);
            if (point) send.current.sendCursor(point.x, point.y);
            else send.current.sendCursor(0, 0, false);
        };
        const onDown = (event: PointerEvent) => {
            if (!userInput.is(event)) return;
            const point = toArea(event);
            if (point) send.current.sendClickMark(point.x, point.y);
        };
        const onLeave = (event: MouseEvent) => {
            if (!event.relatedTarget) send.current.sendCursor(0, 0, false);
        };
        const onBlur = () => send.current.sendCursor(0, 0, false);
        window.addEventListener('pointermove', onMove, { capture: true, passive: true });
        window.addEventListener('pointerdown', onDown, { capture: true, passive: true });
        document.addEventListener('mouseout', onLeave, true);
        window.addEventListener('blur', onBlur);
        return () => {
            window.removeEventListener('pointermove', onMove, { capture: true });
            window.removeEventListener('pointerdown', onDown, { capture: true });
            document.removeEventListener('mouseout', onLeave, true);
            window.removeEventListener('blur', onBlur);
            hide(0, 0, false);
        };
    }, [watched, areaRef, send]);

    return createPortal(
        <SupportPresenceLayer
            tone={tone}
            plain={plain}
            ripples={ripples}
            beacons={drawn.beacons}
            annotations={shapes}
            focus={focus}
        >
            <PresenceCursors presence={presence} area={area} people={people} name={name} plain={plain} />
        </SupportPresenceLayer>,
        supportOverlayRoot(),
    );
};

export default SupportPresenceOverlay;
